import { useEffect, useState, useCallback } from 'react';
import {
  getMetabaseQuestions,
  getDashboardItems,
  createDashboardItem,
  getIframeUrl,
  DashboardItem,
  MetabaseQuestion,
} from '@/api/dashboardItems';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Loader2 } from 'lucide-react';

const REFRESH_INTERVAL_MS = 9 * 60 * 1000; // 9 minutes

export default function DashboardItemsPage() {
  const userId = Number(localStorage.getItem('userId')) || 0;
  const userEmail = localStorage.getItem('userEmail') || '';
  const userType = (() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return '';
      const parsed = JSON.parse(raw);
      return typeof parsed?.type === 'string' ? parsed.type.toUpperCase() : '';
    } catch {
      return '';
    }
  })();

  const [items, setItems] = useState<DashboardItem[]>([]);
  const [iframeUrls, setIframeUrls] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState<MetabaseQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [customName, setCustomName] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState<MetabaseQuestion | null>(null);
  const [resourceType, setResourceType] = useState('question');
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await getDashboardItems(userId);
      setItems(data);
      const urls: Record<number, string> = {};
      await Promise.all(
        data.map(async (item) => {
          try {
            urls[item.id] = await getIframeUrl(userId, item.id, userEmail, userType || undefined);
          } catch {
            urls[item.id] = '';
          }
        })
      );
      setIframeUrls(urls);
    } catch (e) {
      console.error('Failed to load dashboard items:', e);
    } finally {
      setLoading(false);
    }
  }, [userId, userEmail]);

  // Initial load
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Auto-refresh iframe URLs every 9 minutes
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!userId || items.length === 0) return;
      const urls: Record<number, string> = {};
      await Promise.all(
        items.map(async (item) => {
          try {
            urls[item.id] = await getIframeUrl(userId, item.id, userEmail, userType || undefined);
          } catch {
            urls[item.id] = iframeUrls[item.id] ?? '';
          }
        })
      );
      setIframeUrls(urls);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [userId, userEmail, userType, items, iframeUrls]);

  async function openDialog() {
    setOpen(true);
    setQuestionsLoading(true);
    try {
      const data = await getMetabaseQuestions(userId);
      setQuestions(data);
    } catch (e) {
      console.error('Failed to load questions:', e);
    } finally {
      setQuestionsLoading(false);
    }
  }

  async function handleSave() {
    if (!customName || !selectedQuestion) return;
    setSaving(true);
    try {
      await createDashboardItem(userId, {
        customName,
        resourceType,
        resourceId: selectedQuestion.id,
        metabaseQuestionName: selectedQuestion.name,
      });
      setOpen(false);
      setCustomName('');
      setSelectedQuestion(null);
      setResourceType('question');
      await loadItems();
    } catch (e) {
      console.error('Failed to save dashboard item:', e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Analytics Dashboard</h1>
          <p className="text-muted-foreground mt-1">View and manage your embedded analytics charts</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Add Chart
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Analytics Chart</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {/* Display Name */}
              <div className="space-y-1.5">
                <Label htmlFor="customName">Display Name</Label>
                <Input
                  id="customName"
                  placeholder="Enter a name for this chart"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                />
              </div>

              {/* Type */}
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={resourceType} onValueChange={setResourceType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="question">Question / Chart</SelectItem>
                    <SelectItem value="dashboard">Dashboard</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Select Chart */}
              <div className="space-y-1.5">
                <Label>Select Chart</Label>
                {questionsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                ) : (
                  <Select
                    value={selectedQuestion ? String(selectedQuestion.id) : ''}
                    onValueChange={(val) => {
                      const q = questions.find((q) => String(q.id) === val);
                      setSelectedQuestion(q ?? null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a chart..." />
                    </SelectTrigger>
                    <SelectContent>
                      {questions.map((q) => (
                        <SelectItem key={q.id} value={String(q.id)}>
                          {q.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <Button
                className="w-full"
                onClick={handleSave}
                disabled={saving || !customName || !selectedQuestion}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Chart'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-24 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
            <p className="text-lg font-medium mb-1">No charts added yet</p>
            <p className="text-sm">Click "Add Chart" to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {items.map((item) => (
            <Card key={item.id}>
              <CardHeader>
                <CardTitle className="text-lg">{item.metabaseQuestionName || item.customName}</CardTitle>
                {item.metabaseQuestionName && item.customName !== item.metabaseQuestionName && (
                  <p className="text-sm text-muted-foreground">{item.customName}</p>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {iframeUrls[item.id] ? (
                  <iframe
                    src={iframeUrls[item.id]}
                    width="100%"
                    height="600"
                    className="rounded-b-lg border-0"
                    title={item.metabaseQuestionName || item.customName}
                  />
                ) : (
                  <div className="flex justify-center items-center h-40 text-sm text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Loading chart...
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
